import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Mail, Phone, ArrowUpRight, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { SectionLabel } from "./Shared";
import { SERVICES } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACTS = [
  { icon: MapPin, label: "Office", value: "Office No. 205, Building 61, Vijay Block, Laxmi Nagar, Delhi – 110092" },
  { icon: Mail, label: "Email", value: "admin@a3networks.co.in", href: "mailto:admin@a3networks.co.in" },
  { icon: Phone, label: "Phone", value: "011-41604464", href: "tel:01141604464" },
];

export default function Contact() {
  const [tab, setTab] = useState("contact");
  const [loadingC, setLoadingC] = useState(false);
  const [loadingQ, setLoadingQ] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [quote, setQuote] = useState({ name: "", email: "", phone: "", company: "", service: "", details: "" });

  const submitContact = async (e) => {
    e.preventDefault();
    setLoadingC(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message sent. Our team will get back to you shortly.");
      setContact({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoadingC(false);
    }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!quote.service) { toast.error("Please select a service."); return; }
    setLoadingQ(true);
    try {
      await axios.post(`${API}/quote`, quote);
      toast.success("Quote request submitted. We'll prepare an estimate for you.");
      setQuote({ name: "", email: "", phone: "", company: "", service: "", details: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoadingQ(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 border-t border-white/5 blueprint-grid" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          {/* Info */}
          <div className="md:col-span-5">
            <SectionLabel num="[ 04 ]">Contact</SectionLabel>
            <h2 className="font-display font-extrabold text-white tracking-tighter leading-[0.95] text-4xl md:text-6xl">
              Let's build your network.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed" style={{ color: "#94A3B8" }}>
              Reach out for project execution, material supply or a detailed quote.
              Our team responds to every enquiry.
            </p>

            <div className="mt-12 space-y-px bg-white/5 border border-white/5">
              {CONTACTS.map((c, i) => {
                const Icon = c.icon;
                const inner = (
                  <div className="card-hover bg-[#0C1322] p-6 flex items-start gap-4" data-testid={`contact-info-${i}`}>
                    <Icon size={20} className="text-cyan shrink-0 mt-1" style={{ color: "#00F0FF" }} />
                    <div>
                      <div className="mono-label" style={{ color: "#64748B" }}>{c.label}</div>
                      <div className="mt-1 text-sm md:text-base text-white leading-relaxed">{c.value}</div>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a key={i} href={c.href} className="block">{inner}</a>
                ) : (
                  <div key={i}>{inner}</div>
                );
              })}
            </div>
          </div>

          {/* Forms */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#0C1322] border border-white/10 p-6 md:p-9"
            >
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid grid-cols-2 w-full bg-[#060A14] border border-white/10 rounded-none p-1 h-auto">
                  <TabsTrigger
                    value="contact"
                    data-testid="tab-contact"
                    className="rounded-none mono-label data-[state=active]:bg-[#0066FF] data-[state=active]:text-white py-2.5"
                  >
                    Contact
                  </TabsTrigger>
                  <TabsTrigger
                    value="quote"
                    data-testid="tab-quote"
                    className="rounded-none mono-label data-[state=active]:bg-[#0066FF] data-[state=active]:text-white py-2.5"
                  >
                    Request a Quote
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="contact" className="mt-7">
                  <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input required placeholder="Full name *" className="field" data-testid="contact-name"
                        value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                      <input required type="email" placeholder="Email *" className="field" data-testid="contact-email"
                        value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input placeholder="Phone" className="field" data-testid="contact-phone"
                        value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                      <input placeholder="Company" className="field" data-testid="contact-company"
                        value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
                    </div>
                    <textarea required rows={5} placeholder="How can we help? *" className="field resize-none" data-testid="contact-message"
                      value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
                    <button type="submit" disabled={loadingC} data-testid="contact-submit-button"
                      className="group w-full flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-4 mono-label transition-colors duration-300 disabled:opacity-60">
                      {loadingC ? <Loader2 size={16} className="animate-spin" /> : <>Send Message <ArrowUpRight size={16} className="cta-arrow" /></>}
                    </button>
                  </form>
                </TabsContent>

                <TabsContent value="quote" className="mt-7">
                  <form onSubmit={submitQuote} className="space-y-4" data-testid="quote-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input required placeholder="Full name *" className="field" data-testid="quote-name"
                        value={quote.name} onChange={(e) => setQuote({ ...quote, name: e.target.value })} />
                      <input required type="email" placeholder="Email *" className="field" data-testid="quote-email"
                        value={quote.email} onChange={(e) => setQuote({ ...quote, email: e.target.value })} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input required placeholder="Phone *" className="field" data-testid="quote-phone"
                        value={quote.phone} onChange={(e) => setQuote({ ...quote, phone: e.target.value })} />
                      <input placeholder="Company" className="field" data-testid="quote-company"
                        value={quote.company} onChange={(e) => setQuote({ ...quote, company: e.target.value })} />
                    </div>
                    <Select value={quote.service} onValueChange={(v) => setQuote({ ...quote, service: v })}>
                      <SelectTrigger className="field rounded-none h-auto py-3.5 text-white data-[placeholder]:text-[#475569]" data-testid="quote-service">
                        <SelectValue placeholder="Select a service *" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0C1322] border-white/10 text-white">
                        {SERVICES.map((s) => (
                          <SelectItem key={s.id} value={s.title} className="focus:bg-[#131C31] focus:text-cyan">
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <textarea rows={4} placeholder="Project details / scope" className="field resize-none" data-testid="quote-details"
                      value={quote.details} onChange={(e) => setQuote({ ...quote, details: e.target.value })} />
                    <button type="submit" disabled={loadingQ} data-testid="quote-submit-button"
                      className="group w-full flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-4 mono-label transition-colors duration-300 disabled:opacity-60">
                      {loadingQ ? <Loader2 size={16} className="animate-spin" /> : <>Submit Quote Request <ArrowUpRight size={16} className="cta-arrow" /></>}
                    </button>
                  </form>
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
